import React, { FC } from "react";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { StaticDatePicker } from "@mui/x-date-pickers-pro";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { StepDots } from "../components/StepDots";

interface ProjectStartDateStepProps {
    orderName: string;
    projectNumber: number;
    startDate: Date | null;
    onDateChange: (date: Date) => void;
    onBack: () => void;
    onNext: () => void;
}

export const ProjectStartDateStep: FC<ProjectStartDateStepProps> = ({
    orderName,
    projectNumber,
    startDate,
    onDateChange,
    onBack,
    onNext,
}) => {
    const dayjsValue = startDate ? dayjs(startDate) : dayjs();
    const deliveryDate = dayjsValue.add(39, "day");

    return (
        <>
            {/* Body */}
            <Box sx={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", p: "28px 36px 24px" }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb="6px">
                    <Typography sx={{ fontSize: 12, color: "var(--text-2)", fontFamily: "CircularStd" }}>
                        Project {projectNumber} ·{" "}
                        <Box component="span" sx={{ color: "var(--text)", fontWeight: 500 }}>
                            {orderName}
                        </Box>
                    </Typography>
                    <StepDots step={2} />
                </Box>

                <Box
                    flex={1}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    textAlign="center"
                    pt="10px"
                >
                    <Typography
                        sx={{
                            fontSize: 28,
                            fontWeight: 700,
                            color: "var(--text)",
                            letterSpacing: -0.6,
                            mb: "6px",
                            fontFamily: "CircularStd",
                        }}
                    >
                        When do you want production to begin?
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 14,
                            color: "var(--text-2)",
                            mb: "24px",
                            maxWidth: 420,
                            fontFamily: "CircularStd",
                        }}
                    >
                        Pick the date you'd like our team to kick off. We'll confirm the final schedule after your order is submitted.
                    </Typography>

                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-us">
                        <StaticDatePicker
                            value={dayjsValue}
                            onChange={(newVal: Dayjs | null) => {
                                if (newVal) onDateChange(newVal.toDate());
                            }}
                            disablePast
                            slots={{ toolbar: () => null, actionBar: () => null } as any}
                            sx={{
                                "& .MuiPickersDay-root.Mui-selected": {
                                    background: "var(--accent)",
                                    // darker-gold hover has no token (--accent-dark is lighter); gold is theme-constant
                                    "&:hover": { background: "#8a6e3f" },
                                },
                                "& .MuiPickersDay-today": {
                                    border: "1.5px solid var(--accent)",
                                },
                                "& .MuiDateCalendar-root": {
                                    width: "100%",
                                    maxWidth: 380,
                                },
                                border: "1px solid var(--border)",
                                borderRadius: "16px",
                                boxShadow: "var(--sh-2)",
                                overflow: "hidden",
                            }}
                        />
                    </LocalizationProvider>

                    {/* Delivery estimate pill */}
                    <Tooltip
                        title="Based on our average production timeline. Final timeline confirmed after kickoff."
                        placement="bottom"
                    >
                        <Box
                            sx={{
                                mt: "18px",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "8px",
                                background: "var(--tint)",
                                color: "var(--accent-text)",
                                px: "16px",
                                py: "10px",
                                borderRadius: "999px",
                                fontSize: 13,
                                fontWeight: 500,
                                fontFamily: "CircularStd",
                                cursor: "default",
                            }}
                        >
                            <CalendarTodayIcon sx={{ fontSize: 14 }} />
                            Starts{" "}
                            <Box component="strong" sx={{ fontWeight: 700 }}>
                                {dayjsValue.format("ddd, MMM D")}
                            </Box>{" "}
                            · Est. delivery{" "}
                            <Box component="strong" sx={{ fontWeight: 700 }}>
                                {deliveryDate.format("MMM D")}
                            </Box>
                        </Box>
                    </Tooltip>
                </Box>
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: "14px 28px",
                    borderTop: "1px solid var(--border)",
                    background: "var(--surface)",
                    flexShrink: 0,
                }}
            >
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={onBack}
                    sx={{
                        textTransform: "capitalize",
                        fontFamily: "CircularStd",
                        color: "var(--text)",
                    }}
                >
                    Back
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    endIcon={<ArrowForwardIcon />}
                    onClick={onNext}
                    sx={{
                        textTransform: "capitalize",
                        fontFamily: "CircularStd",
                        borderRadius: "24px",
                        px: 3,
                    }}
                >
                    Next
                </Button>
            </Box>
        </>
    );
};
